import * as yup from 'yup'
import { TFunction } from 'i18next'
import { CommissionPolicyScope } from '@/modules/Affiliate/types'

export interface PolicyFormData {
    policy_name: string
    affiliate_id?: number | ''
    affiliate_ids?: number[]
    is_global: boolean
    scope: CommissionPolicyScope
    commission_percent: number
    product_group_id?: number
    product_id?: number
    effective_date: string
    expired_date?: string
    priority: number
    description?: string
    // Audience filter: '' referrer_type = applies to everyone; department_ids only for employees.
    referrer_type?: string
    department_ids?: number[]
}

export const createPolicySchema = (t: TFunction) =>
    yup.object().shape({
        policy_name: yup
            .string()
            .required(t('policy_name') + ' is required')
            .max(255),
        scope: yup
            .string()
            .oneOf(['default', 'product_group', 'product'])
            .required(t('scope') + ' is required'),
        affiliate_id: yup
            .number()
            .transform((value, originalValue) =>
                originalValue === '' ? undefined : value
            )
            .nullable(),
        is_global: yup.boolean().default(false),
        // A policy is either global or targeted. "Targeted at nobody" is what used to be
        // silently reinterpreted as "targeted at everybody".
        affiliate_ids: yup
            .array()
            .of(yup.number().required())
            .default([])
            .when('is_global', {
                is: false,
                then: (schema) => schema.min(1, t('owner_required')),
                otherwise: (schema) =>
                    schema.max(0, t('owner_global_no_select')),
            }),
        commission_percent: yup
            .number()
            .required(t('commission_percent') + ' is required')
            .min(0, 'Min 0%')
            .max(100, 'Max 100%'),
        product_group_id: yup.number().when('scope', {
            is: 'product_group',
            then: (schema) =>
                schema.required(t('product_group') + ' is required'),
            otherwise: (schema) => schema.nullable(),
        }),
        product_id: yup.number().when('scope', {
            is: 'product',
            then: (schema) => schema.required(t('product') + ' is required'),
            otherwise: (schema) => schema.nullable(),
        }),
        effective_date: yup
            .string()
            .required(t('effective_date') + ' is required'),
        expired_date: yup.string().nullable(),
        priority: yup.number().default(0),
        description: yup.string().nullable(),
        referrer_type: yup
            .string()
            .oneOf(
                [
                    '',
                    'partner',
                    'affiliate',
                    'employee',
                    'customer',
                    'other',
                    'reseller',
                ],
                t('invalid_referrer_type')
            )
            .default(''),
        // Departments only make sense for employees; block the combination otherwise.
        department_ids: yup
            .array()
            .of(yup.number().required())
            .default([])
            .when('referrer_type', {
                is: 'employee',
                then: (schema) => schema,
                otherwise: (schema) =>
                    schema.max(0, t('department_requires_employee')),
            }),
    })
